import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { viewPDF } from "../../../utils/viewPDF";
import axios from "axios";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { uploadFile } from "../../../utils/uploadToAzure";
import InfoIcon from "@mui/icons-material/Info";

const Step1 = ({
  tender,
  qDetails,
  setQDetails,
  setStep,
  storedUser,
  fileNames,
  setFileNames,
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [uploadLoading, setUploadLoading] = useState({
    businessRegistration: false,
    businessLicense: false,
    insurance: false,
    compliance: false,
    bankAccount: false,
  });

  const setFieldLoading = (field, isLoading) => {
    setUploadLoading((prev) => ({
      ...prev,
      [field]: isLoading,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const data = qDetails || {};
    const isEmpty = (value) => !value || value.trim?.() === "";

    // Basic required fields
    if (isEmpty(data.name)) newErrors.name = "Name is required";
    if (isEmpty(data.mobile)) {
      newErrors.mobile = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(data.mobile)) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    if (isEmpty(data.email)) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (isEmpty(data.pan)) {
      newErrors.pan = "PAN is required";
    } else if (!/[A-Z]{5}[0-9]{4}[A-Z]{1}/.test(data.pan)) {
      newErrors.pan = "Enter a valid PAN (e.g., ABCDE1234F)";
    }

    if (isEmpty(data.address)) newErrors.address = "Address is required";

    if (
      (!fileNames.businessRegistration && !data.businessRegistration) ||
      errors.businessRegistration
    )
      newErrors.businessRegistration = "Business registration file is required";

    if (isEmpty(data.gstin)) {
      newErrors.gstin = "GSTIN is required";
    } else {
      const gstinRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinRegex.test(data.gstin)) {
        newErrors.gstin = "Enter a valid GSTIN";
      }
    }

    if (isEmpty(data.bankName)) newErrors.bankName = "Bank name is required";
    if (isEmpty(data.accountNumber))
      newErrors.accountNumber = "Account number is required";

    if (isEmpty(data.ifsc)) {
      newErrors.ifsc = "IFSC code is required";
    } else {
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscRegex.test(data.ifsc)) {
        newErrors.ifsc = "Enter a valid IFSC code (e.g., SBIN0001234)";
      }
    }
    if (isEmpty(data.accountType))
      newErrors.accountType = "Account type is required";
    if (isEmpty(data.isMSME)) newErrors.isMSME = "This field is required";
    if (
      (!fileNames.businessLicense && !data.businessLicense) ||
      errors.businessLicense
    )
      newErrors.businessLicense = "Business license file is required";
    if ((!fileNames.insurance && !data.insurance) || errors.insurance)
      newErrors.insurance = "Insurance file is required";
    if ((!fileNames.compliance && !data.compliance) || errors.compliance)
      newErrors.compliance = "Compliance certificate is required";
    if ((!fileNames.bankAccount && !data.bankAccount) || errors.bankAccount)
      newErrors.bankAccount = "Bank account details is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const updatedValue =
      e.target.name === "pan" ||
      e.target.name === "gstin" ||
      e.target.name === "ifsc"
        ? e.target.value.toUpperCase()
        : e.target.value;
    setQDetails({ ...qDetails, [e.target.name]: updatedValue });
    setErrors({ ...errors, [e.target.name]: "" });
  };
  const handleNext = () => {
    if (!validateForm()) return;
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    navigate("/vendor/dashboard");
  };

  const handleFileUpload = async (e) => {
    const { name, files } = e.target;
    setFieldLoading(name, true);
    if (files && files.length > 0) {
      const file = files[0]; // Get the first file

      // Update the file names state
      setFileNames((prev) => ({
        ...prev,
        [name]: file.name, // Set the filename in state
      }));

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await uploadFile(
          file,
          tender.orgId,
          tender.tenderId,
          storedUser
        );
        // Set the received file path in tender state
        setQDetails({ ...qDetails, [e.target.name]: res });
        setErrors({ ...errors, [e.target.name]: "" });
      } catch (error) {
        setErrors({
          ...errors,
          [e.target.name]:
            "Cannot upload file. Make sure file size is less than 1MB",
        });
      } finally {
        setFieldLoading(name, false);
      }
    }
  };

  return (
    <div>
      <Box className="dashboard-container" px={10}>
        <Typography variant="h6">Basic Information</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="name">Name (Firm Name)</label>
            <input
              type="text"
              id="name"
              name="name"
              onChange={handleChange}
              value={qDetails && qDetails.name}
              className={`${errors.name && "input-error"}`}
            />
            {errors.name && <p className="error">{errors.name}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="mobile">Phone Number (Contact person)</label>
            <input
              type="text"
              id="mobile"
              name="mobile"
              onChange={handleChange}
              value={qDetails && qDetails.mobile}
              className={`${errors.mobile && "input-error"}`}
            />
            {errors.mobile && <p className="error">{errors.mobile}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="email">Email ID</label>
            <input
              type="email"
              id="email"
              name="email"
              onChange={handleChange}
              value={qDetails && qDetails.email}
              className={`${errors.email && "input-error"}`}
            />
            {errors.email && <p className="error">{errors.email}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              onChange={handleChange}
              value={qDetails && qDetails.address}
              className={`${errors.address && "input-error"}`}
            />
            {errors.address && <p className="error">{errors.address}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="businessRegistration">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "4px",
                }}
              >
                Business Registration Certificates
                <Tooltip title={<>
      Certificate of Incorporation / Registration <br />
      Registered Partnership Certificate <br />
      Sole Proprietorship Registration documents <br />
      GST Registration Certificate <br />
      MSME/Udyam Registration Certificate
    </>}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
              <div
                className={`input-file ${
                  errors.businessRegistration && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="businessRegistration"
                  name="businessRegistration"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.businessRegistration || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.businessRegistration && (
                <p className="error">{errors.businessRegistration}</p>
              )}
              {uploadLoading.businessRegistration && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.businessRegistration &&
                !uploadLoading.businessRegistration && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() =>
                      viewPDF(qDetails.businessRegistration, storedUser)
                    }
                  >
                    {fileNames.businessRegistration}
                  </Button>
                )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box className="dashboard-container" px={10}>
        <Typography variant="h6">Financial Information</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="pan">PAN</label>
            <input
              type="text"
              id="pan"
              name="pan"
              onChange={handleChange}
              value={qDetails && qDetails.pan}
              className={`${errors.pan && "input-error"}`}
            />
            {errors.pan && <p className="error">{errors.pan}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="gstin">GSTIN</label>
            <input
              type="text"
              id="gstin"
              name="gstin"
              onChange={handleChange}
              value={qDetails && qDetails.gstin}
              className={`${errors.gstin && "input-error"}`}
            />
            {errors.gstin && <p className="error">{errors.gstin}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="bankName">Bank Name</label>
            <input
              type="text"
              id="bankName"
              name="bankName"
              onChange={handleChange}
              value={qDetails && qDetails.bankName}
              className={`${errors.bankName && "input-error"}`}
            />
            {errors.bankName && <p className="error">{errors.bankName}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="accountNumber">Bank Account Number</label>
            <input
              type="text"
              id="accountNumber"
              name="accountNumber"
              onChange={handleChange}
              value={qDetails && qDetails.accountNumber}
              className={`${errors.accountNumber && "input-error"}`}
            />
            {errors.accountNumber && (
              <p className="error">{errors.accountNumber}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="ifsc">IFSC Code</label>
            <input
              type="text"
              id="ifsc"
              name="ifsc"
              onChange={handleChange}
              value={qDetails && qDetails.ifsc}
              className={`${errors.ifsc && "input-error"}`}
            />
            {errors.ifsc && <p className="error">{errors.ifsc}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="accountType">Account Type</label>
            <input
              type="text"
              id="accountType"
              name="accountType"
              onChange={handleChange}
              value={qDetails && qDetails.accountType}
              className={`${errors.accountType && "input-error"}`}
            />
            {errors.accountType && (
              <p className="error">{errors.accountType}</p>
            )}
          </Grid2>
        </Grid2>
      </Box>

      <Box className="dashboard-container" px={10}>
        <Typography variant="h6">Compliance and Legal</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="isMSME">Do you belong to MSME?</label>
            <select
              name="isMSME"
              id="isMSME"
              className={`${errors.isMSME && "input-error"}`}
              onChange={handleChange}
              value={qDetails && qDetails.isMSME}
            >
              <option value="">-Choose-</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
            {errors.isMSME && <p className="error">{errors.isMSME}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="businessLicense">
              Business License (GST registration/Pan)
              <div
                className={`input-file ${
                  errors.businessLicense && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="businessLicense"
                  name="businessLicense"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.businessLicense || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {errors.businessLicense && (
              <p className="error">{errors.businessLicense}</p>
            )}
            {uploadLoading.businessLicense && (
              <span style={{ fontSize: "12px" }}>
                Uploading File... <CircularProgress size="30px" />
              </span>
            )}
            {qDetails &&
              qDetails.businessLicense &&
              !uploadLoading.businessLicense && (
                <Button
                  sx={{
                    margin: "-6% 0 0 0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(qDetails.businessLicense, storedUser)}
                >
                  {fileNames.businessLicense}
                </Button>
              )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="insurance">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "4px",
                }}
              >
                Insurance Certificates
                <Tooltip
                  title={
                    "Workmen’s Compensation / Employee Insurance/insurance undertaking"
                  }
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
              <div
                className={`input-file ${errors.insurance && "input-error"}`}
              >
                <input
                  type="file"
                  id="insurance"
                  name="insurance"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.insurance || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {errors.insurance && <p className="error">{errors.insurance}</p>}
            {uploadLoading.insurance && (
              <span style={{ fontSize: "12px" }}>
                Uploading File... <CircularProgress size="30px" />
              </span>
            )}
            {qDetails && qDetails.insurance && !uploadLoading.insurance && (
              <Button
                sx={{
                  margin: "-6% 0 0 0 !important",
                  width: "max-content !important",
                  fontSize: "12px !important",
                  textDecoration: "underline",
                  textAlign: "start",
                }}
                onClick={() => viewPDF(qDetails.insurance, storedUser)}
              >
                {fileNames.insurance}
              </Button>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="compliance">
              Compliance with industry-specific regulations
              <div
                className={`input-file ${errors.compliance && "input-error"}`}
              >
                <input
                  type="file"
                  id="compliance"
                  name="compliance"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.compliance || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {errors.compliance && <p className="error">{errors.compliance}</p>}
            {uploadLoading.compliance && (
              <span style={{ fontSize: "12px" }}>
                Uploading File... <CircularProgress size="30px" />
              </span>
            )}
            {qDetails && qDetails.compliance && !uploadLoading.compliance && (
              <Button
                sx={{
                  margin: "-6% 0 0 0 !important",
                  width: "max-content !important",
                  fontSize: "12px !important",
                  textDecoration: "underline",
                  textAlign: "start",
                }}
                onClick={() => viewPDF(qDetails.compliance, storedUser)}
              >
                {fileNames.compliance}
              </Button>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="bankAccount">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "4px",
                }}
              >
                Bank Account Details
                <Tooltip title={"Cancelled Cheque/Bank Account Statement"}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
              <div
                className={`input-file ${errors.bankAccount && "input-error"}`}
              >
                <input
                  type="file"
                  id="bankAccount"
                  name="bankAccount"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.bankAccount || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {errors.bankAccount && (
              <p className="error">{errors.bankAccount}</p>
            )}
            {uploadLoading.bankAccount && (
              <span style={{ fontSize: "12px" }}>
                Uploading File... <CircularProgress size="30px" />
              </span>
            )}
            {qDetails && qDetails.bankAccount && !uploadLoading.bankAccount && (
              <Button
                sx={{
                  margin: "-6% 0 0 0 !important",
                  width: "max-content !important",
                  fontSize: "12px !important",
                  textDecoration: "underline",
                  textAlign: "start",
                }}
                onClick={() => viewPDF(qDetails.bankAccount, storedUser)}
              >
                {fileNames.bankAccount}
              </Button>
            )}
          </Grid2>
        </Grid2>
      </Box>
      <Box className="btn-container" sx={{ gap: 5 }}>
        <Button className="btn-outlined" sx={{ px: 7 }} onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" sx={{ px: 7 }} onClick={handleNext}>
          Next
        </Button>
      </Box>
    </div>
  );
};

export default Step1;
